import React, { useEffect, useState } from 'react';
import { toast } from "react-toastify";
import { ModeEditOutlineTwoTone } from "@mui/icons-material";
import { Box, CircularProgress, IconButton, Typography, Switch } from "@mui/material";

import AppTable from "components/Shared/AppTable";
import FormDrawer from "components/Shared/FormDrawer";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import { useShipmentDetailContext } from "contexts/ShipmentDetailContext";
import { useTablePagination } from "hooks";
import {
  useLazyListShipmentRoutesQuery,
  useUpdateShipmentRouteMutation,
  useAddShipmentRouteMutation,
} from "store/services/shipmentApi";
import ShipmentRoutesForm from "./ShipmentRoutesForm";

const ShipmentRouteList = ({ latestEndStop, setLatestEndStop }) => {
  const [showRouteForm, setShowRouteForm] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState(null);

  const { currentPage, perPage, handlePageChange, handlePerRowsChange } = useTablePagination();
  const { ShipmentDetail } = useShipmentDetailContext();

  // RTK Query
  const [updateRoute, { isLoading: updatingRoute }] = useUpdateShipmentRouteMutation();
  const [addRoute, { isLoading: addingRoute }] = useAddShipmentRouteMutation();
  const [fetchRoutes, { data: routesRes, isLoading, isFetching }] = useLazyListShipmentRoutesQuery();

  // Handle status toggle
  const handleStatusChange = async (row) => {
    try {
      if (!ShipmentDetail?._id || !row?._id) {
        toast.error("Shipment details or route ID not available");
        return;
      }

      const newStatus = !row.isActive;

      await updateRoute({
        routeId: row._id,
        shipmentId: ShipmentDetail._id,
        account: row.account?._id,
        entity: row.entity?._id,
        isActive: newStatus,
      }).unwrap();

      toast.success(`Route ${newStatus ? 'activated' : 'deactivated'} successfully!`);

      // Refresh the routes list
      fetchRoutes({ shipmentId: ShipmentDetail._id, page: currentPage, limit: perPage });
    } catch (err) {
      toast.error(err?.data?.message || "Failed to update route status!");
    }
  };

  const columns = [
    {
      name: "Route Name",
      selector: (row) => row?.route?.routeName || '',
      cell: (row) => row?.route?.routeName || 'N/A',
      sortable: true,
    },
    {
      name: "Start Stop",
      selector: (row) => row?.routeStartStop?.name || '',
      cell: (row) => row?.routeStartStop?.name
        ? `${row.routeStartStop.name} - ${row.routeStartStop.code || 'N/A'}`
        : 'N/A',
      sortable: true,
    },
    {
      name: "End Stop",
      selector: (row) => row?.routeEndStop?.name || '',
      cell: (row) => row?.routeEndStop?.name
        ? `${row.routeEndStop.name} - ${row.routeEndStop.code || 'N/A'}`
        : 'N/A',
      sortable: true,
    },
    {
      name: "Comments",
      selector: (row) => row?.comments || '',
      cell: (row) => row?.comments || 'N/A',
      sortable: true,
    },
    {
      name: "Distance",
      selector: (row) => row?.distance || '',
      cell: (row) => row?.distance || 'N/A',
      sortable: true,
    },
    {
      name: "Actions",
      cell: (row) => (
        <Box display="flex" alignItems="center" gap={1}>
          <Switch
            checked={row.isActive === true}
            onChange={() => handleStatusChange(row)}
            color="primary"
            size="small"
            inputProps={{ "aria-label": "Status Toggle" }}
            disabled={ShipmentDetail?.isActive === false || updatingRoute}
          />
          <IconButton
            size="small"
            disabled={ShipmentDetail?.isActive === false}
            aria-label="Edit Route"
            onClick={() => {
              if (!row?._id) {
                toast.error("Cannot edit route: Missing route ID");
                return;
              }
              setSelectedRoute(row);
              setShowRouteForm(true);
            }}
          >
            <ModeEditOutlineTwoTone />
          </IconButton>
        </Box>
      ),
      ignoreRowClick: true,
      right: true,
    },
  ];

  // Methods
  const handleCancelRoute = () => {
    setShowRouteForm(false);
    setSelectedRoute(null);
  };

  const handleAddRoute = async (data) => {
    try {
      if (!ShipmentDetail?._id) {
        toast.error("Shipment details not available");
        return;
      }
      if (!data.account || !data.entity || !data.route || !data.routeStartStop || !data.routeEndStop) {
        toast.error("All fields are required");
        return;
      }

      await addRoute({
        shipmentId: ShipmentDetail._id,
        account: data.account,
        entity: data.entity,
        route: data.route,
        routeStartStop: data.routeStartStop,
        routeEndStop: data.routeEndStop,
        comments: data.comments,
      }).unwrap();
      toast.success("Route added successfully!");
      setLatestEndStop(data.routeEndStop);
      setShowRouteForm(false); // Close form after adding
      fetchRoutes({ shipmentId: ShipmentDetail._id, page: currentPage, limit: perPage });
    } catch (err) {
      toast.error(err?.data?.message || "Failed to add route!");
    }
  };

  const handleUpdateRoute = async (data) => {
    try {
      if (!ShipmentDetail?._id) {
        toast.error("Shipment details not available");
        return;
      }
      if (!data.routeId) {
        toast.error("Route ID is missing");
        return;
      }
      if (!/^[0-9a-fA-F]{24}$/.test(data.routeId)) {
        toast.error("Invalid route ID format");
        return;
      }

      // Only update comments
      const body = {
        account: data.account,
        entity: data.entity,
        route: data.route,
        routeStartStop: data.routeStartStop,
        routeEndStop: data.routeEndStop,
        comments: data.comments,
      };
      await updateRoute({
        routeId: data.routeId,
        shipmentId: ShipmentDetail._id,
        ...body,
      }).unwrap();
      toast.success("Route comments updated successfully!");
      setShowRouteForm(false);
      setSelectedRoute(null);
      fetchRoutes({ shipmentId: ShipmentDetail._id, page: currentPage, limit: perPage });
    } catch (err) {
      toast.error(err?.data?.message || "Failed to update route comments!");
    }
  };

  useEffect(() => {
    if (!ShipmentDetail?._id) {
      return;
    }
    fetchRoutes({
      shipmentId: ShipmentDetail._id,
      page: currentPage,
      limit: perPage,
    });
  }, [ShipmentDetail?._id, currentPage, perPage, fetchRoutes]);

  return (
    <div>
      <BreadcrumbLayout.Paper>
        {routesRes ? (
          <AppTable
            columns={columns}
            data={Array.isArray(routesRes?.data) ? routesRes.data : []}
            progressPending={isLoading || isFetching || updatingRoute}
            progressComponent={
              <Box display="flex" alignItems="center" padding={2}>
                <CircularProgress size={24} />
                <Typography variant="body2" marginLeft={1}>
                  Loading Shipment Routes...
                </Typography>
              </Box>
            }
            paginationServer
            paginationPerPage={perPage}
            paginationTotalRows={routesRes?.totalItems ?? 0}
            paginationDefaultPage={currentPage}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handlePerRowsChange}
            noDataComponent={
              <Typography variant="body2" padding={2}>
                No shipment routes found.
              </Typography>
            }
          />
        ) : (
          <Box display="flex" alignItems="center" justifyContent="center" padding={2}>
            <CircularProgress size={24} />
            <Typography variant="body2" marginLeft={1}>
              Loading Shipment Routes...
            </Typography>
          </Box>
        )}
      </BreadcrumbLayout.Paper>
      <FormDrawer
        title={selectedRoute ? "Edit Route" : "Add Route"}
        open={showRouteForm}
        onClose={handleCancelRoute}
      >
        <ShipmentRoutesForm
          isBusy={updatingRoute || addingRoute}
          route={selectedRoute}
          latestEndStop={latestEndStop}
          setLatestEndStop={setLatestEndStop}
          shipmentId={ShipmentDetail?._id}
          onSubmit={selectedRoute ? handleUpdateRoute : handleAddRoute}
          onCancel={handleCancelRoute}
        />
      </FormDrawer>
    </div>
  );
};

export default ShipmentRouteList;